<template>
  <div>
    <b-modal
      id="modal-invoice-tasks"
      content-class="modal-fullscreen"
      body-class="modal-body-fullscreen"
      size="xl"
      ref="modalInvoiceTasksSelect"
      :title="title"
      :ok-only="false"
      :no-fade="true"
      @ok="rowClickHandler"
      @cancel="cancelHandler"
      @close="cancelHandler"
      ok-title="Добавить в счёт"
      cancel-title="Отменить"
      no-close-on-esc
      no-close-on-backdrop
      scrollable
    >
      <b-row class="mb-0">
        <b-col cols="10">
          <b-card no-body>
            <b-card no-body class="mb-2">
              <b-table
                id="invoice-tasks"
                ref="invoiceTasksSelectTable"
                class="mb-0"
                striped
                small
                hover
                thead-class="top-fixed-thead thead-light"
                table-class="text-small"
                responsive
                show-empty
                :empty-text="$t('message.common.emptyTable')"
                :busy.sync="isBusy"
                :items="items"
                :fields="fields"
                @row-clicked="rowClicked"
                :tbody-tr-class="tbodyRowClass"
                no-provider-filtering
                no-provider-paging
                no-provider-sorting
              >
                <template #head(selected)="row">
                  <b-form-checkbox
                    class="mx-1"
                    :checked="allSelected"
                    :indeterminate="indeterminate"
                    :disabled="allSelectDisabled"
                    size="md"
                    @change="selectAllRows"
                  />
                </template>

                <template #cell(selected)="row">
                  <template v-if="row.item.selected">
                    <span aria-hidden="true">&check;</span>
                    <span class="sr-only">Выбрана</span>
                  </template>
                  <template v-else>
                    <span >&nbsp;</span>
                    <span class="sr-only">Не выбрана</span>
                  </template>
                </template>

                <template #cell(start_address)="row">
                  <div v-if="row.item.start_address.length<50">{{ row.item.start_address }}</div>
                  <div v-else>{{ row.item.start_address.substring(0, 50) + ".." }}</div>
                </template>

                <template #cell(start_date)="row">
                  {{formatTableDateTime(row.item.start_date)}}
                </template>

                <template #cell(total_price)="row">
                  {{priceFormat(row.item.total_price)}}
                </template>

                <template #cell(status_id)="row">
                  <div style="font-size: medium">
                    <b-badge pill :variant="getStatusStyle(row.item.status_id)">
                      {{getStatusName(row.item.status_id)}}
                    </b-badge>
                  </div>
                </template>

                <template #cell(paid_workers)="row">
                  <font-awesome-icon :icon="['fas', paidWorkers(row).icon]" :style=paidWorkers(row).style />
                </template>

                <template #cell(paid)="row">
                  {{paid(row)}}
                </template>

                <template #cell(read_only)="row">
                  <font-awesome-icon :icon="['fas', readOnly(row).icon]" :style=readOnly(row).style />
                </template>
              </b-table>
            </b-card>

            <pagination-footer
              :total-rows="totalRows"
              :per-page.sync="perPage"
              @page-click="onPagination"
            >
              <template #footer-list>
                <div>
                  Всего заявок: <b>{{totalRows}}</b>
                </div>
              </template>
              <template #footer-buttons>
                <b-pagination
                  pills
                  :total-rows="totalRows"
                  :per-page="perPage"
                  v-model="currentPage"
                  @page-click="onPagination"
                />
              </template>
            </pagination-footer>

          </b-card>
        </b-col>
        <b-col cols="2">
          <div class="mr-1" style="position: sticky; top:0">
            <b-row class="mb-2">
              <b-input-group>
                <b-form-input size="sm" v-model="query" :placeholder="$t('message.common.search')" @input="onQueryChange"></b-form-input>
                <b-input-group-append>
                  <b-btn size="sm" variant="info" @click.stop="onSearch()">
                    <font-awesome-icon icon="search"/> Найти
                  </b-btn>
                  <b-btn :disabled="query.length === 0" size="sm" variant="secondary" @click.stop="clearSearch()">
                    <font-awesome-icon icon="times-circle"/>
                  </b-btn>
                </b-input-group-append>
              </b-input-group>
            </b-row>
            <b-row>
              <b-form-group
                id="group_town"
                class="w-100"
                label="Город"
                label-for="input_town">
                <b-form-select
                  size="sm"
                  id="input_town"
                  v-model="currentTown"
                  @change="onTownChange()"
                >
                  <option :value="-1">Все города</option>
                  <option v-for="option in towns" :value="option.id">
                    {{ option.name }}
                  </option>
                </b-form-select>
              </b-form-group>
            </b-row>

            <b-row>
              <b-form-group
                id="group_location"
                label="Объекты"
              >
                <location-multi-select
                  id="location-multi-select"
                  ref="location_multi_select"
                  @onSelect="onSelectLocation"
                  :start-date="startDate"
                  :end-date="endDate"
                  :show-date-button="true"
                />
              </b-form-group>

              <b-form-group
                id="group_customer"
                :label="$t('message.tasks.modal.customer')"
                label-for="input_customer">
                <b-input-group>
                  <b-form-input
                    id="input_customer"
                    type="text"
                    size="sm"
                    readonly
                    :placeholder="$t('message.tasks.modal.customerPlaceholder')"
                    v-model="customer.name">
                  </b-form-input>
                  <b-input-group-append>
                    <b-btn size="sm" variant="outline-success" @click.stop="showCustomerTable()">Выбрать</b-btn>
                    <b-btn :disabled="customer.id === 0" size="sm" variant="secondary" @click.stop="removeCustomer()">
                      <font-awesome-icon icon="times-circle"/>
                    </b-btn>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>

              <b-form-group
                id="group_paid"
                class="w-100"
                label="Оплачено"
                label-for="input_paid"
              >
                <b-form-select id="input_paid" size="sm" v-model="filterPaid" :options="filterPaidTypes" @change="onSearch"></b-form-select>
              </b-form-group>

              <b-form-group
                id="group_executor"
                class="w-100"
                label="Менеджер"
                label-for="input_executor"
              >
              <b-form-select
                id="input_executors"
                v-model="executor_id"
                size="sm"
                @change="onSearch()"
              >
                <option :value="0">Все</option>
                <option v-for="option in activeExecutors" :value="option.id">
                  {{ option.name }}
                </option>
              </b-form-select>
              </b-form-group>

              <b-form-group
                id="group_start_date"
                label="Дата начала с"
                label-for="input_start_date"
              >
                <b-form-input size="sm" id="input_start_date" type="date" v-model="startDate" @change="onSearch()" />
              </b-form-group>

              <b-form-group
                id="group_end_date"
                class="pl-2"
                label="по"
                label-for="input_end_date"
              >
                <b-form-input size="sm" id="input_end_date" type="date" v-model="endDate" @change="onSearch()" />
              </b-form-group>

              <b-form-group
                id="group_statuses"
                label="Статус заявки"
                label-for="input_statuses"
              >
                <b-form-select
                  id="input_statuses"
                  class="mb-1 mr-2"
                  size="sm"
                  v-model="status_id"
                  @change="onSearch()"
                >
                  <option :value="0">Все статусы</option>
                  <option v-for="option in statuses" :value="option.id">
                    {{ option.name }}
                  </option>
                </b-form-select>
              </b-form-group>

              <b-form-group
                id="group_statuses"
                label="Оплачено исполнителям"
                label-for="input_statuses"
              >
                <b-form-select
                  id="input_paid_workers_statuses"
                  class="mb-1 mr-2"
                  size="sm"
                  v-model="paid_workers_status_id"
                  @change="onSearch()"
                >
                  <option :value="0">Все статусы оплаты</option>
                  <option v-for="option in paidWorkersStatuses" :value="option.id">
                    {{ option.name }}
                  </option>
                </b-form-select>
              </b-form-group>
            </b-row>
          </div>
        </b-col>
      </b-row>

      <template #modal-footer="{ ok, cancel }">
        <h6>Будут добавлены: {{ selected.length }}</h6>
        <div class="vr"/>
        <h6>Уже в добавлены: {{ invoiceTasks.length }}</h6>
        <div class="vr"/>
        <b-button size="md" variant="success" @click="ok()">
          Добавить в счёт
        </b-button>
        <b-button size="md" variant="secondary" @click="cancel()">
          Закрыть
        </b-button>
      </template>
    </b-modal>

    <customer-table-select ref="customerTable" :town="0" v-on:selectCustomer="updateCustomer"/>
  </div>
</template>

<script>
import tasks from "../../network/tasks";
import users from "../../network/users";
import catalogs from "../../network/catalogs";
  import helpers from "../../utilites/helpers";
  import dateFormatter from "../../utilites/dateFormatter";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import {debounce} from "lodash";
    import CustomerTableSelect from "../Customers/CustomerTableSelect";
    import LocationMultiSelect from "../Locations/LocationMultiSelect.vue";

export default {
  name: "InvoiceTasksSelect",
  components: { LocationMultiSelect, PaginationFooter, CustomerTableSelect },
  mixins: [
    tasks, users, catalogs,
    helpers, dateFormatter, pageSearchService
  ],
  props: ["tasks"],
  data() {
    return {
      title: 'Заявки',
      isBusy: false,
      items: [],
      fields: [
        {
          key: 'selected',
          label: '',
          class:'tasksSelect'
        },
        {
          key: 'id',
          sortable: true,
        },
        {
          key: 'customer_name',
          label: this.$t('message.tasks.table.customer'),
          sortable: true,
        },
        {
          key: 'location_name',
          label: 'Объект',
          sortable: true,
        },
        {
          key: 'start_date',
          label: this.$t('message.tasks.table.start_date'),
          sortable: true,
          class:'widthDate'
        },
        {
          key: 'payment_first',
          label: 'Стоимость ед.',
          sortable: true,
        },
        {
          key: 'total_hours',
          label: 'Кол-во часов',
          sortable: true,
        },
        {
          key: 'workers_count',
          label: this.$t('message.tasks.table.workers_count'),
          sortable: true,
        },
        {
          key: 'total_price',
          label: this.$t('message.tasks.table.total_price'),
          sortable: true,
        },
        {
          key: 'paid_workers',
          label: this.$t('message.tasks.table.paid_workers'),
          sortable: true,
        },
        {
          key: 'status_id',
          label: 'Статус',
          sortable: false,
        },
        {
          key: 'executor_name',
          label: this.$t('message.tasks.table.executor_name'),
          sortable: true,
        },
        {
          key: 'paid',
          label: this.$t('message.tasks.table.paid'),
          sortable: true,
        },
      ],
      sortBy: 'id',
      sortDesc: true,
      perPage: 150,
      selected: [],
      allSelected: false,
      indeterminate: false,
      allSelectDisabled: false,
      invoiceTasks: [],
      towns: [],
      locations: [],
      executors: [],
      statuses: [],
      query: '',
      executor_id: 0,
      customer_id: 0,
      status_id: 2,
      currentTown: -1,
      filterPaid: -1,
      startDate: null,
      endDate: null,
      customer: {
        id: 0,
        name: ''
      },
      filterPaidTypes: [
        { value: -1, text: "Все" },
        { value: 0, text: "Не оплачено" },
        { value: 1, text: "Оплачено" },
      ],
      paidWorkersStatuses: [
        {id: 1, name: 'Не оплачено'},
        {id: 2, name: 'Оплачено'},
      ],
      paid_workers_status_id: 0
    }
  },
  async created() {
    if (!this.$store.getters.isLoggedIn) {
      await this.$router.push('/login').catch(()=>{});
      return;
    }

    let date = this.getNewDateToServer();
    date.setDate(date.getDate() - 60);
    this.startDate = this.formatDate(date);

    await this.getExecutors();

    this.towns = (await this.getTowns({})).data.items;

    this.statuses = (await this.getTaskStatuses()).data.items;

    await this.getItems();
    this.invoiceTasksDebouncedSearch = debounce(this.onSearch, 700);
  },
  computed: {
    activeExecutors() {
      return this.executors.filter(item => item.active)
    }
  },
  methods: {
    // Modal
    async showModalSelect() {
      this.invoiceTasks = this.tasks;
      this.$refs.modalInvoiceTasksSelect.show();

      await this.getItems();
    },
    hideModal() {
      this.$refs.modalInvoiceTasksSelect.hide();
    },
    async getExecutors() {
      const result = await this.getUsers();

      if (!result || !result.data) {
        this.executors = [];
        return;
      }

      this.executors = result.data.users;
    },
    async getItems() {
      const locationIds = this.locations.map(function (item) {
        return "&locations[]=" + item;
      });

      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.query,
        customer_id: this.customer_id,
        paid: this.filterPaid,
        executor: this.executor_id,
        town: this.currentTown,
        locations: locationIds.join(''),
        status_id: this.status_id,
        paid_workers: this.paid_workers_status_id,
        startDate: this.startDate,
        endDate: this.endDate,
      }

      this.isBusy = true;
      const result = await this.getInvoiceTasks(data);

      if (!result || !result.data) {
        this.items = [];
        this.totalRows = 0;
        this.isBusy = false;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.items = result.data.tasks;
      this.totalRows = result.data.total;
      this.updateSelected();
      this.isBusy = false;
    },
    // Select
    updateSelected() {
      if (!this.items) return;

      this.items.forEach((item) => {
        const isAssigned = this.invoiceTasks.some(e => e.id === item.id);
        const isSelected = this.selected.some(e => e.id === item.id);

        this.$set(item, 'selected', isSelected);
        item._rowVariant = isAssigned ? 'danger' : '';
      });
    },
    tbodyRowClass(item) {
      return item && item.selected ? ["b-table-row-selected", "table-primary", "pointer-cell"] : ["pointer-cell"];
    },
    rowClicked(item) {
      // Ищем уже добавленные в заявку
      if (this.invoiceTasks.some(e => e.id === item.id)) {
        return;
      }

      // Ищем уже выбранные
      const existItem = this.selected.findIndex(e => e.id === item.id);

      if (existItem!== -1) {
        this.selected.splice(existItem, 1);
        this.$set(item, 'selected', false);
      } else {
        this.selected.push(item);
        this.$set(item, 'selected', true);
      }
    },
    selectAllRows(checked) {
      if (checked) {
        this.items.forEach(item => {
          // Пропускаем те, что уже есть в счете
          if (!this.invoiceTasks.some(e => e.id === item.id)) {
            if (!this.selected.some(e => e.id === item.id)) {
              this.selected.push(item);
            }
          }
        });
      } else {
        this.items.forEach(item => {
          const index = this.selected.findIndex(e => e.id === item.id);
          if (index !== -1) {
            this.selected.splice(index, 1);
          }
        });
      }
      this.updateSelected();
    },
    rowClickHandler() {
      this.$emit('addToInvoice', this.selected);
      this.selected = [];
      this.hideModal();
    },
    cancelHandler() {
      this.selected = [];
    },
    showCustomerTable() {
      this.$refs.customerTable.showModalSelect();
    },
    removeCustomer() {
      this.customer = {
        id: 0,
        name: ''
      };
      this.customer_id = 0;
      this.onSearch();
    },
    updateCustomer(customer) {
      this.customer = customer;
      this.customer_id = customer.id;
      this.onSearch();
    },
    // Search
    async onSearch() {
      this.currentPage = 1;
      await this.getItems();
    },
    async onPagination(e, page) {
      this.currentPage = page;
      await this.getItems();
    },
    clearSearch() {
      this.query = '';
      this.customer_id = 0;
      this.onSearch();
    },
    paidWorkers(row) {
      return this.yesOrNoIcon(row.item.paid_workers);
    },
    paid(row) {
      return this.yesOrNoText(row.item.paid);
    },
    onQueryChange() {
      this.invoiceTasksDebouncedSearch();
    },
    onTownChange() {
      this.currentPage = 1;
      this.getItems();
    },
    // Status
    getStatusName(status_id) {
      const status = this.statuses.find(e => e.id === status_id)
      return status != null ? status.name : 'Ошибка';
    },
    getStatusStyle(status_id) {
      switch (status_id) {
        case 1:
          return 'primary';
        case 2:
          return 'info';
        case 3:
          return 'secondary';
        case 4:
          return 'success';
        default:
          return '';
      }
    },
    onSelectLocation(locations) {
      this.locations = locations.map(function (item) { return item.id; });
      this.currentPage = 1;
      this.invoiceTasksDebouncedSearch();
    },
  },
  watch: {
    selected: {
      handler(newValue, oldValue) {
        const selectableItems = this.items ? this.items.filter(item => !this.invoiceTasks.find(e => e.id === item.id)) : [];
        const selectedCount = newValue.length;
        const totalCount = selectableItems.length;

        if (selectedCount === 0) {
          this.allSelected = false;
          this.indeterminate = false;
        } else if (selectedCount === totalCount && totalCount > 0) {
          this.allSelected = true;
          this.indeterminate = false;
        } else {
          this.allSelected = false;
          this.indeterminate = true;
        }
      }, deep: true,
    },
    totalRows(newValue, oldValue) {
      this.allSelectDisabled = newValue === 0 || newValue > 150;
    }
  }
}
</script>

<style>
#invoice-tasks td {
  text-align: center;
}

#invoice-tasks th {
  text-align: center;
  vertical-align: middle;
}

#modal-invoice-tasks .modal-dialog {
  max-width: 100% !important;
  max-height: 100% !important;
  height: 100% !important;
  margin: 0 !important;
}

.modal-fullscreen {
  max-height: 100% !important;
  height: 100% !important;
}

.modal-body-fullscreen {
  padding-bottom: 0  !important;
}

#footer {
  position: sticky;
  bottom: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/Invoices/InvoiceTasksSelect.vue